import { InfoCard } from 'components/UI/InfoCard'
import React, { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import apiService from 'services/api'
import { formatInteger, formatUsd } from 'utils/formatting'
import { SalesForecastTargetLineModal } from '../Modals/SalesForecastTargetLineModal'
import { MultiSelect } from '../UI/Select/MultiSelect'
import { Select } from '../UI/Select/Select'
import { featureFlagService } from 'utils/featureFlagService'
import { usePreferences } from 'context/preferences/PreferencesContext'

const horizonOptions = [
  {
    label: '12 Months',
    value: 12,
  },
  {
    label: '24 Months',
    value: 24,
  },
  {
    label: '36 Months',
    value: 36,
  },
]

const chartOptions = [
  {
    label: 'Revenue',
    value: 'forecasted_revenue',
  },
  {
    label: 'Volume',
    value: 'forecasted_volume',
  },
]

export function SalesForecast() {
  const api = apiService()
  const { preferences, isLoading: isPreferencesLoading } = usePreferences()

  const [product, setProduct] = React.useState<number[]>([])
  const [user, setUser] = React.useState<number[]>([])
  const [stage, setStage] = React.useState<number[]>([])
  const [distributor, setDistributor] = React.useState<number[]>([])
  const [gpo, setGpo] = React.useState<number[]>([])
  const [tag, setTag] = React.useState<number[]>([])
  const [subCatId, setSubCatId] = React.useState<number[]>([])
  const [selectedCampaigns, setSelectedCampaigns] = React.useState<number[]>([])
  const [horizon, setHorizon] = React.useState<number>(horizonOptions[0].value)
  const [chart, setChart] = React.useState<string>(chartOptions[0].value)
  const { data: companyCatAndSub, isLoading: isOppLoading } =
    api.useGetActiveCompanyCategoriesAndTypes()
  const { data: products } = api.useGetProductsOptions()
  const { data: stages } = api.useGetSalesStageOptions()
  const { data: users } = api.useGetUserOptions({ onlyActive: true })
  const { data: campaigns } = api.useGetCampaigns()
  const { data: distributors } = api.useGetCompanyDistributorsOptions()
  const { data: tagsData } = api.useGetTagsOptions()
  const { data: gposData } = api.useGetGPOs()
  const gpos = useMemo(() => {
    return gposData?.map((gpo) => ({
      label: gpo.name,
      value: gpo.id,
    }))
  }, [gposData])
  const tags = useMemo(() => {
    return tagsData?.map((tag) => ({
      label: tag.label,
      value: tag.value as number,
    }))
  }, [tagsData])
  const { enableDashboardReports } = featureFlagService()

  const reportingUnit = preferences?.reporting_unit || 'lbs'

  const { data: salesForecast, isLoading: isSalesForecastLoading } =
    api.useGetDashboardSalesForecast(
      enableDashboardReports,
      horizon,
      user,
      product,
      stage,
      subCatId,
      selectedCampaigns,
      distributor,
      gpo,
      tag
    )

  // Wait for both sales forecast AND preferences to load
  const isLoading = isSalesForecastLoading || isPreferencesLoading

  const subCatOptions = useMemo(() => {
    return (
      companyCatAndSub?.flatMap((cat) =>
        cat.sub_categories.map((subCat) => ({
          label: subCat.name,
          value: subCat.id,
        }))
      ) ?? []
    )
  }, [companyCatAndSub])

  const colors = useMemo(() => {
    const part = 75 / (horizon ?? 1)
    const _colors: string[] = []
    for (let i = 0; i < horizon; i++) {
      const opacity = (0.25 + (part * (i + 1)) / 100).toFixed(2)
      _colors.push(`rgba(6,81,47,${opacity})`)
    }
    return _colors
  }, [horizon])

  const campaignOptions = useMemo(() => {
    const options = campaigns?.results.map((c) => ({
      label: c.name,
      value: c.id,
      icon: (
        <div
          className="h-3 w-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: c.color }}
        />
      ),
    }))

    options?.push({
      label: 'Without Campaign',
      value: -1,
      icon: (
        <div
          className="h-3 w-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: '#fff' }}
        />
      ),
    })

    return options ?? []
  }, [campaigns])

  return (
    <div>
      <h2 className="uppercase font-light text-2xl text-gray-700">
        Rolling Sales Forecast
      </h2>

      <hr className="border-gray-400 mb-6 mt-2" />

      <div className="grid gap-3 mb-10 mt-2 grid-cols-3">
        <MultiSelect
          disabled={isOppLoading}
          onSelect={setSubCatId}
          selected={subCatId}
          buttonVariant={'secondary'}
          buttonLabel={'Opp. Type'}
          items={subCatOptions}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search opportunity types..."
          notFoundPlaceholder="No opportunity types found."
        />
        <MultiSelect
          onSelect={setSelectedCampaigns}
          selected={selectedCampaigns}
          buttonVariant={'secondary'}
          buttonLabel={'Campaign'}
          items={campaignOptions}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search campaigns..."
          notFoundPlaceholder="No campaigns found."
        />
        <MultiSelect
          onSelect={setProduct}
          selected={product}
          buttonVariant={'secondary'}
          buttonLabel={'Product'}
          items={products}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search products..."
          notFoundPlaceholder="No products found."
        />
        <MultiSelect
          onSelect={setUser}
          selected={user}
          buttonVariant={'secondary'}
          buttonLabel={'Account Owner'}
          items={users}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search account owners..."
          notFoundPlaceholder="No account owners found."
        />
        <MultiSelect
          onSelect={setStage}
          selected={stage}
          buttonVariant={'secondary'}
          buttonLabel={'Sales Stage'}
          items={stages?.map((s) => ({
            ...s,
            icon: (
              <div
                className={`h-3 w-3 rounded-full flex-shrink-0`}
                style={{ backgroundColor: s.color }}
              />
            ),
          }))}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search sales stages..."
          notFoundPlaceholder="No sales stages found."
        />
        <Select
          onSelect={setHorizon}
          defaultSelection={horizonOptions[0].value}
          selected={horizon}
          buttonVariant={'secondary'}
          buttonLabel={'Time Horizon'}
          items={horizonOptions}
          searchPlaceholder="Search time horizons..."
          notFoundPlaceholder="No time horizons found."
        />
        <Select
          defaultSelection={chartOptions[0].value}
          onSelect={setChart}
          selected={chart}
          buttonVariant={'secondary'}
          buttonLabel={'Chart Type'}
          items={chartOptions}
          searchPlaceholder="Search chart types..."
          notFoundPlaceholder="No chart types found."
        />

        <MultiSelect
          onSelect={setDistributor}
          selected={distributor}
          buttonVariant={'secondary'}
          buttonLabel={'Distributor'}
          items={distributors}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search distributors..."
          notFoundPlaceholder="No distributors found."
        />

        <MultiSelect
          onSelect={setGpo}
          selected={gpo}
          buttonVariant={'secondary'}
          buttonLabel={'GPO'}
          items={gpos}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search GPO..."
          notFoundPlaceholder="No GPO found."
        />

        <MultiSelect
          onSelect={setTag}
          selected={tag}
          buttonVariant={'secondary'}
          buttonLabel={'Tag'}
          items={tags}
          selectAllBehavior={'selectNone'}
          enableSelectAll
          searchPlaceholder="Search tags..."
          notFoundPlaceholder="No tags found."
        />
      </div>

      <div className="pr-2">
        {isLoading ? (
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-[8]" style={{ width: '100%' }}>
              <div className="h-[355px] bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="flex-[2] flex lg:flex-col gap-2 flex-wrap">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="p-3 bg-gray-200 rounded-xl border-gray-300 border h-20 animate-pulse"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-[8]" style={{ width: '100%' }}>
              {salesForecast && colors && (
                <BarChart
                  key={reportingUnit}
                  targetValue={
                    preferences?.misc_settings?.find(
                      (it) => it.key === 'show_sales_forecast_line'
                    )?.value
                      ? preferences?.misc_settings?.find(
                          (it) => it.key === 'sales_forecast_target_line'
                        )?.value
                      : undefined
                  }
                  values={salesForecast?.charts?.[chart].values}
                  labels={salesForecast?.charts?.[chart].labels}
                  colors={colors}
                  formatTootltipCurrency={chart === 'forecasted_revenue'}
                  formatYAxisCurrency={chart === 'forecasted_revenue'}
                  reportingUnit={reportingUnit}
                />
              )}
            </div>
            <div className="flex-[2] flex lg:flex-col gap-2 flex-wrap">
              {salesForecast?.cards?.map((item, index) => (
                <InfoCard
                  key={index}
                  title={item.label}
                  value={item.value}
                  tooltip={item?.tooltip}
                />
              ))}
              <SalesForecastTargetLineModal />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function BarChart(props: {
  values: number[]
  labels: string[]
  colors: string[]
  formatTootltipCurrency?: boolean
  targetValue?: number
  formatYAxisCurrency?: boolean
  reportingUnit?: string
}) {
  const max = roundChartPadding(
    Math.max(...props.values, props.targetValue ?? 0)
  )

  const chartData = React.useMemo(
    () => ({
      labels: props.labels,
      datasets: [
        {
          data: props.values,
          backgroundColor: props.colors,
          borderRadius: 4,
          borderColor: 'transparent',
          borderWidth: 2,
          datalabels: {
            display: false,
          },
        },
      ],
    }),
    [props.labels, props.values, props.colors]
  )

  const chartOptions = React.useMemo(
    () => ({
      plugins: {
        annotation: {
          annotations: {
            line1: {
              type: 'line' as const,
              display: !!props.targetValue,
              yMin: props.targetValue,
              yMax: props.targetValue,
              borderColor: '#E2E4E9',
              borderWidth: 3,
              borderDash: [6, 4],
            },
          },
        },
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: '#fff',
          titleColor: '#667085',
          titleFont: {
            weight: 'lighter' as const,
            size: 14,
          },
          borderWidth: 1,
          borderColor: '#c2c2c2',
          bodyColor: '#18181B',
          bodyFont: {
            weight: 'bold' as const,
            size: 14,
          },
          bodyAlign: 'center' as const,
          displayColors: false,
          yAlign: 'bottom' as const,

          callbacks: {
            label: function (context: any) {
              if (props.formatTootltipCurrency) {
                return formatUsd(context.parsed.y)
              }
              return (
                formatInteger(context.parsed.y) +
                ` ${props.reportingUnit || 'lbs'}/month`
              )
            },
          },
        },
      },
      layout: {},
      scales: {
        y: {
          suggestedMax: max,
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
          ticks: {
            display: true,
            callback: function (value: any) {
              const numValue =
                typeof value === 'string' ? parseFloat(value) : value
              if (props.formatYAxisCurrency) {
                return formatUsd(numValue)
              }
              return formatInteger(numValue)
            },
          },
        },
        x: {
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
          ticks: {
            display: true,
          },
        },
      },
    }),
    [
      props.targetValue,
      props.formatTootltipCurrency,
      props.reportingUnit,
      props.formatYAxisCurrency,
      max,
    ]
  )

  return (
    <Bar
      style={{ width: '100%', maxHeight: '355px' }}
      data={chartData}
      options={chartOptions}
    />
  )
}

function roundChartPadding(number: number) {
  if (number < 1000000) {
    // Round to the nearest ten thousand
    return Math.round(number / 10000) * 10000
  } else {
    // Round to the nearest hundred thousand
    return Math.round(number / 100000) * 100000
  }
}
